import { useEffect, useRef, useState, useCallback } from 'react'
import './FalseColor.css'

function FalseColor({ videoRef, imageRef, useSMPTE = false, seekTime = 0, is2X = false, onToggle2X }) {
  const canvasRef = useRef(null)
  const containerRef = useRef(null)
  const animationFrameRef = useRef(null)
  const [isActive, setIsActive] = useState(true)
  const [opacity, setOpacity] = useState(0.7)
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 })
  const lastSeekTimeRef = useRef(seekTime)
  const lastFrameTimeRef = useRef(0)
  const tempCanvasRef = useRef(null)
  const TARGET_FPS = 30
  const FRAME_INTERVAL = 1000 / TARGET_FPS

  // False color mapping function - maps luma (0-255) to RGB color
  // Professional false color IRE mapping for exposure analysis
  const getFalseColor = useCallback((luma) => {
    // Convert luma to IRE (0-100 scale)
    const ire = (luma / 255) * 100

    // Professional false color IRE mapping
    if (ire < 0) {
      return { r: 0, g: 0, b: 0 } // Black
    } else if (ire < 4) {
      // 0-4 IRE: Purple (Crushed blacks/detail loss)
      const t = ire / 4
      return { r: Math.floor(128 * t), g: 0, b: Math.floor(128 * t) }
    } else if (ire < 5) {
      // 4-5 IRE: Transition Purple to Blue
      const t = (ire - 4) / 1
      return { 
        r: Math.floor(128 * (1 - t)), 
        g: 0, 
        b: Math.floor(128 + 127 * t) 
      }
    } else if (ire < 41) {
      // 5-41 IRE: Blue transitioning to Green (via Cyan)
      const t = (ire - 5) / 36
      return { 
        r: 0, 
        g: Math.floor(64 + 191 * t), 
        b: 255 
      }
    } else if (ire < 48) {
      // 41-48 IRE: Green (Middle gray/mid-tones, good for skin tones)
      const t = (ire - 41) / 7
      return { r: 0, g: 255, b: Math.floor(255 - 255 * t) }
    } else if (ire < 61) {
      // 48-61 IRE: Transition Green to Pink/Magenta (via Yellow-Green)
      const t = (ire - 48) / 13
      return { 
        r: Math.floor(128 * t), 
        g: 255, 
        b: Math.floor(255 * (1 - t)) 
      }
    } else if (ire < 70) {
      // 61-70 IRE: Pink/Magenta (Proper skin tone)
      const t = (ire - 61) / 9
      return { 
        r: 255, 
        g: Math.floor(128 + 127 * (1 - t)), 
        b: Math.floor(128 + 127 * (1 - t)) 
      }
    } else if (ire < 90) {
      // 70-90 IRE: Transition Pink/Magenta to Yellow/Orange
      const t = (ire - 70) / 20
      return { 
        r: 255, 
        g: Math.floor(255 - 64 * t), 
        b: Math.floor(255 * (1 - t)) 
      }
    } else if (ire < 98) {
      // 90-98 IRE: Yellow/Orange (Highlights approaching clipping)
      const t = (ire - 90) / 8
      return { 
        r: 255, 
        g: Math.floor(191 - 64 * t), 
        b: 0 
      }
    } else if (ire < 99) {
      // 98-99 IRE: Transition Yellow/Orange to Red
      const t = (ire - 98) / 1
      return { 
        r: 255, 
        g: Math.floor(127 - 127 * t), 
        b: 0 
      }
    } else if (ire < 100) {
      // 99-100 IRE: Red (Blown-out, clipped whites)
      return { r: 255, g: 0, b: 0 }
    } else {
      // 100+ IRE: Bright Red/White (Fully clipped)
      return { r: 255, g: 0, b: 0 }
    }
  }, [])

  // Resize canvas to fit container
  useEffect(() => {
    const container = containerRef.current
    const canvas = canvasRef.current
    if (!container || !canvas) return

    const resizeCanvas = () => {
      const rect = container.getBoundingClientRect()
      const headerHeight = 60
      const availableHeight = rect.height - headerHeight
      const availableWidth = rect.width - 32
      
      const newWidth = Math.floor(availableWidth)
      const newHeight = Math.floor(Math.max(200, availableHeight))
      
      // Only update if size changed
      if (canvas.width !== newWidth || canvas.height !== newHeight) {
        canvas.width = newWidth
        canvas.height = newHeight
        setCanvasSize({ width: newWidth, height: newHeight })
      }
    }

    resizeCanvas()
    const resizeObserver = new ResizeObserver(resizeCanvas)
    resizeObserver.observe(container)

    return () => resizeObserver.disconnect()
  }, [])

  useEffect(() => {
    const canvas = canvasRef.current
    const video = useSMPTE ? null : videoRef.current
    const image = useSMPTE ? imageRef.current : null
    const source = useSMPTE ? image : video
    if (!canvas || !source) return

    if (!tempCanvasRef.current) {
      tempCanvasRef.current = document.createElement('canvas')
    }

    lastSeekTimeRef.current = seekTime

    const ctx = canvas.getContext('2d', {
      alpha: false,
      desynchronized: true,
      willReadFrequently: false
    })
    const width = canvas.width
    const height = canvas.height

    const drawFalseColor = (currentTime = performance.now()) => {
      const seekChanged = seekTime !== lastSeekTimeRef.current
      const isVideoReady = !useSMPTE && video && video.readyState >= 2 && !video.paused
      const isImageReady = useSMPTE && image && image.complete
      const shouldUpdate = isActive && (isVideoReady || isImageReady || seekChanged)

      const timeSinceLastFrame = currentTime - lastFrameTimeRef.current
      const shouldDraw = shouldUpdate && (seekChanged || timeSinceLastFrame >= FRAME_INTERVAL)

      if (!shouldDraw) {
        animationFrameRef.current = requestAnimationFrame(drawFalseColor)
        return
      }

      if (seekChanged) {
        lastSeekTimeRef.current = seekTime
      }
      lastFrameTimeRef.current = currentTime

      // Get source dimensions
      const sourceWidth = useSMPTE ? (image.naturalWidth || 640) : (video.videoWidth || 640)
      const sourceHeight = useSMPTE ? (image.naturalHeight || 360) : (video.videoHeight || 360)

      const tempCanvas = tempCanvasRef.current
      if (tempCanvas.width !== sourceWidth || tempCanvas.height !== sourceHeight) {
        tempCanvas.width = sourceWidth
        tempCanvas.height = sourceHeight
      }

      const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: false })
      tempCtx.drawImage(source, 0, 0, sourceWidth, sourceHeight)
      const imageData = tempCtx.getImageData(0, 0, sourceWidth, sourceHeight)
      const data = imageData.data

      // Calculate display dimensions maintaining aspect ratio
      const videoAspect = sourceWidth / sourceHeight
      const canvasAspect = width / height
      
      let displayWidth, displayHeight, offsetX, offsetY
      
      if (canvasAspect > videoAspect) {
        // Canvas is wider - fit to height
        displayHeight = height
        displayWidth = height * videoAspect
        offsetX = (width - displayWidth) / 2
        offsetY = 0
      } else {
        // Canvas is taller - fit to width
        displayWidth = width
        displayHeight = width / videoAspect
        offsetX = 0
        offsetY = (height - displayHeight) / 2
      }

      // Clear canvas
      ctx.fillStyle = '#000'
      ctx.fillRect(0, 0, width, height)

      // Create blended image with false color overlay
      const outputImageData = ctx.createImageData(width, height)
      const outputData = outputImageData.data

      const scaleX = sourceWidth / displayWidth
      const scaleY = sourceHeight / displayHeight

      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const dstIdx = (y * width + x) << 2
          
          // Check if pixel is within the display area
          if (x >= offsetX && x < offsetX + displayWidth && 
              y >= offsetY && y < offsetY + displayHeight) {
            // Map to source coordinates
            const srcX = Math.floor((x - offsetX) * scaleX)
            const srcY = Math.floor((y - offsetY) * scaleY)

            if (srcX >= 0 && srcX < sourceWidth && srcY >= 0 && srcY < sourceHeight) {
              const srcIdx = (srcY * sourceWidth + srcX) << 2

              // Calculate luma using Rec. 709 coefficients
              const r = data[srcIdx]
              const g = data[srcIdx + 1]
              const b = data[srcIdx + 2]
              const luma = Math.floor(0.2126 * r + 0.7152 * g + 0.0722 * b)

              // Get false color
              const falseColor = getFalseColor(luma)

              // Blend original with false color based on opacity
              outputData[dstIdx] = Math.floor(r * (1 - opacity) + falseColor.r * opacity)
              outputData[dstIdx + 1] = Math.floor(g * (1 - opacity) + falseColor.g * opacity)
              outputData[dstIdx + 2] = Math.floor(b * (1 - opacity) + falseColor.b * opacity)
              outputData[dstIdx + 3] = 255
            } else {
              // Black for out of bounds
              outputData[dstIdx] = 0
              outputData[dstIdx + 1] = 0
              outputData[dstIdx + 2] = 0
              outputData[dstIdx + 3] = 255
            }
          } else {
            // Black for areas outside display
            outputData[dstIdx] = 0
            outputData[dstIdx + 1] = 0
            outputData[dstIdx + 2] = 0
            outputData[dstIdx + 3] = 255
          }
        }
      }

      // Draw blended image with false color overlay
      ctx.putImageData(outputImageData, 0, 0)

      // Title
      ctx.fillStyle = '#fff'
      ctx.font = 'bold 14px monospace'
      ctx.textAlign = 'left'
      ctx.fillText('False Color', 10, 20)

      animationFrameRef.current = requestAnimationFrame(drawFalseColor)
    }

    drawFalseColor()

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
    }
  }, [videoRef, imageRef, useSMPTE, isActive, seekTime, opacity, canvasSize, getFalseColor])

  return (
    <div className="false-color" ref={containerRef}>
      <div className="false-color-header">
        <h2>False Color</h2>
        <div className="header-controls">
          <label className="opacity-control">
            <span>Opacity:</span>
            <input
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={opacity}
              onChange={(e) => setOpacity(parseFloat(e.target.value))}
              className="opacity-slider"
            />
            <span className="opacity-value">{(opacity * 100).toFixed(0)}%</span>
          </label>
          <button
            className="toggle-button"
            onClick={() => setIsActive(!isActive)}
          >
            {isActive ? 'Pause' : 'Resume'}
          </button>
          {onToggle2X && (
            <button
              className={`toggle-button ${is2X ? 'active' : ''}`}
              onClick={onToggle2X}
              title="2X Width (Desktop only)"
            >
              2X
            </button>
          )}
        </div>
      </div>
      <canvas
        ref={canvasRef}
        className="false-color-canvas"
      />
      <div className="false-color-legend">
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#800080' }}></span>
          <span>0-4 IRE (Crushed)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#0000ff' }}></span>
          <span>~5 IRE (Near-black)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#00ff00' }}></span>
          <span>41-48 IRE (Mid-tones)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#ff00ff' }}></span>
          <span>61-70 IRE (Skin tone)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#ff8000' }}></span>
          <span>90-98 IRE (Highlights)</span>
        </div>
        <div className="legend-item">
          <span className="legend-color" style={{ backgroundColor: '#ff0000' }}></span>
          <span>99-100 IRE (Clipped)</span>
        </div>
      </div>
    </div>
  )
}

export default FalseColor

